import { AbsoluteFill, interpolate, useCurrentFrame } from "remotion";
import {
  Caption,
  Callout,
  CodePanel,
  COLORS,
  FONTS,
  MetricCounter,
  Panel,
  PipelineGraph,
  SceneTransition,
  SlideTimeBar,
  type PipeNode,
} from "../../coldcompute";
import { SCENE } from "./constants";
import {
  HOOK,
  REVEAL,
  DIAGNOSIS,
  TRAP,
  CODE,
  PAYOFF,
  LESSON,
  OUTRO,
} from "./script";

const center: React.CSSProperties = {
  alignItems: "center",
  justifyContent: "center",
  flexDirection: "column",
};

const Kicker: React.FC<{ text: string; color: string }> = ({ text, color }) => (
  <div
    style={{
      fontFamily: FONTS.mono,
      fontSize: 22,
      letterSpacing: "0.22em",
      textTransform: "uppercase",
      color,
      marginBottom: 22,
    }}
  >
    ▍{text}
  </div>
);

const Big: React.FC<{ children: React.ReactNode; size?: number }> = ({
  children,
  size = 92,
}) => (
  <div
    style={{
      fontFamily: FONTS.display,
      fontWeight: 700,
      fontSize: size,
      lineHeight: 0.98,
      letterSpacing: "-0.02em",
      color: COLORS.text,
      textAlign: "center",
      maxWidth: 1560,
    }}
  >
    {children}
  </div>
);

/* 01 — HOOK : chat timing out, error hidden as [object Object] */
export const Scene01Hook: React.FC = () => {
  const frame = useCurrentFrame();
  const six = interpolate(frame, [40, 80], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });
  return (
    <AbsoluteFill style={center}>
      <SlideTimeBar sceneDurationFrames={SCENE.hook.duration} />
      <SceneTransition from="up">
        <AbsoluteFill style={center}>
          <Kicker text="a debugging war story" color={COLORS.amber} />
          <Big>
            <span style={{ color: COLORS.red, opacity: 0.4 + six * 0.6 }}>
              6 seconds
            </span>{" "}
            to search.
            <br />
            <span style={{ color: COLORS.dim }}>The error was hidden as</span>{" "}
            <span style={{ fontFamily: FONTS.mono, color: COLORS.amber }}>
              [object Object]
            </span>
          </Big>
        </AbsoluteFill>
      </SceneTransition>
      <Caption lines={HOOK} accent="amber" />
    </AbsoluteFill>
  );
};

/* 02 — REVEAL : the real error — statement timeout · 57014 */
export const Scene02Reveal: React.FC = () => {
  const frame = useCurrentFrame();
  // a "latency" bar fills toward 6s, then a timeout stamp slams in at 8s
  const ms = interpolate(frame, [12, 120], [0, 6013], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });
  const stamp = interpolate(frame, [150, 178], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });
  // fill width is latency / 8000ms timeout (so 6013ms ≈ 75% of the track)
  const pct = Math.min((ms / 8000) * 100, 100);
  return (
    <AbsoluteFill style={center}>
      <SlideTimeBar sceneDurationFrames={SCENE.reveal.duration} />
      <SceneTransition from="up">
        <AbsoluteFill style={center}>
          <Kicker text="the real error · surfaced from logs" color={COLORS.red} />
          <Panel accent="red" title="hybrid_search · query latency" width={1040}>
            {/* latency track — 8s is the statement_timeout wall */}
            <div
              style={{
                position: "relative",
                height: 30,
                background: COLORS.bg2,
                borderRadius: 6,
                overflow: "hidden",
                border: `1px solid ${COLORS.line}`,
              }}
            >
              <div
                style={{
                  height: "100%",
                  width: `${pct}%`,
                  background: COLORS.red,
                  boxShadow: `0 0 22px -4px ${COLORS.red}`,
                }}
              />
            </div>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "baseline",
                marginTop: 16,
                fontFamily: FONTS.mono,
                fontSize: 22,
                color: COLORS.dim,
              }}
            >
              <span>statement_timeout = 8,000 ms</span>
              <span style={{ color: COLORS.red, fontSize: 30 }}>
                {Math.round(ms).toLocaleString()} ms
              </span>
            </div>
            <div
              style={{
                fontFamily: FONTS.mono,
                fontSize: 20,
                color: COLORS.red,
                marginTop: 14,
                opacity: stamp,
                border: `1px solid ${COLORS.red}`,
                borderRadius: 6,
                padding: "8px 14px",
                display: "inline-block",
              }}
            >
              ▍canceling statement due to statement timeout · code 57014
            </div>
          </Panel>
        </AbsoluteFill>
      </SceneTransition>
      <Caption lines={REVEAL} accent="red" />
    </AbsoluteFill>
  );
};

/* 03 — DIAGNOSIS : the ORDER BY — CASE sits before the distance operator */
const BUG_SQL = `-- hybrid_search_document_chunks  ·  the vector pass
SELECT chunk_id, embedding <=> query AS distance
FROM   document_chunks
ORDER BY
  -- ✗ a CASE expression sits BEFORE the distance operator
  CASE WHEN has_preferred THEN (...) ELSE false END DESC,
  embedding <=> query        -- the index can't help anymore
LIMIT k;`;
export const Scene03Diagnosis: React.FC = () => (
  <AbsoluteFill style={center}>
    <SlideTimeBar sceneDurationFrames={SCENE.diagnosis.duration} />
    <AbsoluteFill style={{ alignItems: "center", justifyContent: "center", paddingBottom: 40 }}>
      <Kicker text="the diagnosis · one line in the ORDER BY" color={COLORS.amber} />
      <CodePanel
        code={BUG_SQL}
        language="sql"
        filename="hybrid_search_document_chunks.sql"
        highlightLines={[6, 7]}
        revealFrames={220}
        fontSize={23}
        width={1280}
      />
    </AbsoluteFill>
    <Caption lines={DIAGNOSIS} accent="amber" />
  </AbsoluteFill>
);

/* 04 — TRAP : HNSW only serves a PURE distance sort → otherwise a seq scan */
export const Scene04Trap: React.FC = () => {
  const frame = useCurrentFrame();
  const reveal = (f: number) =>
    interpolate(frame, [f, f + 18], [0, 1], {
      extrapolateLeft: "clamp",
      extrapolateRight: "clamp",
    });
  return (
    <AbsoluteFill style={center}>
      <SlideTimeBar sceneDurationFrames={SCENE.trap.duration} />
      <SceneTransition from="up">
        <AbsoluteFill style={center}>
          <Kicker text="the trap · how HNSW gives up" color={COLORS.magenta} />
          <div style={{ display: "flex", alignItems: "stretch", gap: 30 }}>
            {/* the only shape HNSW can serve */}
            <div style={{ opacity: reveal(8) }}>
              <Panel accent="green" title="HNSW accelerates" width={470}>
                <div
                  style={{
                    fontFamily: FONTS.mono,
                    fontSize: 19,
                    lineHeight: 1.7,
                    color: COLORS.text,
                  }}
                >
                  ORDER BY
                  <br />
                  &nbsp;&nbsp;embedding &lt;=&gt; query
                  <br />
                  LIMIT k
                  <br />
                  <span style={{ color: COLORS.green }}>↳ pure distance · index used</span>
                </div>
              </Panel>
            </div>
            {/* what the leading expression turns it into */}
            <div style={{ opacity: reveal(44) }}>
              <Panel accent="red" title="any leading expression" width={470}>
                <div
                  style={{
                    fontFamily: FONTS.mono,
                    fontSize: 19,
                    lineHeight: 1.7,
                    color: COLORS.text,
                  }}
                >
                  ORDER BY
                  <br />
                  &nbsp;&nbsp;CASE … DESC,
                  <br />
                  &nbsp;&nbsp;embedding &lt;=&gt; query
                  <br />
                  <span style={{ color: COLORS.red }}>↳ index abandoned · SEQ SCAN</span>
                </div>
              </Panel>
            </div>
          </div>
          <div
            style={{
              opacity: reveal(96),
              marginTop: 34,
              fontFamily: FONTS.mono,
              fontSize: 22,
              color: COLORS.red,
              border: `1px solid ${COLORS.red}55`,
              borderRadius: 8,
              padding: "10px 18px",
            }}
          >
            exact cosine distance across ALL ~24,837 vectors → full sort → ~6 s
          </div>
        </AbsoluteFill>
      </SceneTransition>
      <Caption lines={TRAP} accent="magenta" />
    </AbsoluteFill>
  );
};

/* 05 — CODE : the fix — nearest-K pure subquery, re-sort in an outer query */
const FIX = `-- fetch nearest-K with a PURE distance sort → HNSW kicks in
WITH candidates AS (
  SELECT chunk_id, embedding, embedding <=> query AS distance
  FROM   document_chunks
  ORDER BY embedding <=> query    -- nothing before the operator
  LIMIT  k                        -- index serves this in ms
)
-- THEN re-sort the tiny candidate set + apply the threshold
SELECT chunk_id, distance
FROM   candidates
WHERE  (1 - distance) >= similarity_threshold
ORDER BY
  CASE WHEN has_preferred THEN (...) ELSE false END DESC,
  distance;`;
export const Scene05Code: React.FC = () => (
  <AbsoluteFill style={center}>
    <SlideTimeBar sceneDurationFrames={SCENE.code.duration} />
    <AbsoluteFill style={{ alignItems: "center", justifyContent: "center", paddingBottom: 40 }}>
      <CodePanel
        code={FIX}
        language="sql"
        filename="hybrid_search_document_chunks.sql"
        highlightLines={[5, 6, 7]}
        revealFrames={300}
        fontSize={22}
        width={1320}
      />
    </AbsoluteFill>
    <Caption lines={CODE} accent="green" />
  </AbsoluteFill>
);

/* 06 — PAYOFF : 6,013 ms → 701 ms · ~8.6× faster */
export const Scene06Payoff: React.FC = () => {
  const frame = useCurrentFrame();
  const c1 = interpolate(frame, [150, 180], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  const c2 = interpolate(frame, [220, 250], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  return (
    <AbsoluteFill style={center}>
      <SlideTimeBar sceneDurationFrames={SCENE.payoff.duration} />
      <AbsoluteFill style={center}>
        <Kicker text="hybrid_search · after the one-line fix" color={COLORS.green} />
        <div style={{ display: "flex", alignItems: "center", gap: 26 }}>
          <div style={{ display: "flex", alignItems: "baseline", gap: 10 }}>
            <span
              style={{
                fontFamily: FONTS.display,
                fontWeight: 700,
                fontSize: 92,
                color: COLORS.dim,
                textDecoration: "line-through",
                textDecorationColor: `${COLORS.red}`,
              }}
            >
              6,013
            </span>
            <span style={{ fontFamily: FONTS.display, fontWeight: 700, fontSize: 40, color: COLORS.dim }}>ms</span>
          </div>
          <span style={{ fontFamily: FONTS.display, fontWeight: 700, fontSize: 64, color: COLORS.green }}>→</span>
          <div style={{ display: "flex", alignItems: "baseline", gap: 10 }}>
            <MetricCounter
              value={701}
              from={6013}
              startFrame={10}
              durationFrames={60}
              accent="green"
              fontSize={150}
              format={(n) => Math.round(n).toLocaleString()}
            />
            <span style={{ fontFamily: FONTS.display, fontWeight: 700, fontSize: 54, color: COLORS.dim }}>ms</span>
          </div>
        </div>
        <div style={{ display: "flex", gap: 30, marginTop: 38 }}>
          <div style={{ opacity: c1 }}>
            <Callout label="speedup" accent="cyan" width={360}>
              <b style={{ color: COLORS.text }}>~8.6×</b> faster · timeout gone
            </Callout>
          </div>
          <div style={{ opacity: c2 }}>
            <Callout label="retrieval" accent="green" width={360}>
              BM25 hybrid search <b style={{ color: COLORS.text }}>restored</b>
            </Callout>
          </div>
        </div>
      </AbsoluteFill>
      <Caption lines={PAYOFF} accent="green" />
    </AbsoluteFill>
  );
};

/* 07 — LESSON : the rule, stated plainly */
const RULE: PipeNode[] = [
  { label: "step 1", value: "nearest-K", sub: "pure distance · LIMIT k", accent: "green" },
  { label: "step 2", value: "re-sort", sub: "preference + threshold", accent: "cyan" },
  { label: "result", value: "index served", sub: "no seq scan", accent: "lime" },
];
export const Scene07Lesson: React.FC = () => (
  <AbsoluteFill style={center}>
    <SlideTimeBar sceneDurationFrames={SCENE.lesson.duration} />
    <AbsoluteFill style={{ ...center, padding: "0 90px" }}>
      <Kicker text="the rule · for any vector ORDER BY" color={COLORS.cyan} />
      <Big size={52}>
        Never put an expression{" "}
        <span style={{ color: COLORS.red }}>before</span> the distance operator
        <br />
        in an ORDER BY you want the index to serve.
      </Big>
      <div style={{ width: 1560, marginTop: 44 }}>
        <PipelineGraph nodes={RULE} startFrame={8} stepFrames={24} nodeWidth={320} />
      </div>
    </AbsoluteFill>
    <Caption lines={LESSON} accent="cyan" />
  </AbsoluteFill>
);

/* 08 — OUTRO */
export const Scene08Outro: React.FC = () => (
  <AbsoluteFill style={center}>
    <SlideTimeBar sceneDurationFrames={SCENE.outro.duration} />
    <SceneTransition from="up">
      <AbsoluteFill style={center}>
        <Big size={66}>
          One line moved the work
          <br />
          off the hot path.
          <br />
          <span style={{ color: COLORS.cyan }}>Fetch nearest-K pure, then re-sort.</span>
        </Big>
        <div
          style={{
            fontFamily: FONTS.mono,
            fontSize: 24,
            letterSpacing: "0.24em",
            color: COLORS.dim,
            marginTop: 44,
          }}
        >
          EAKC · debugging in public
        </div>
      </AbsoluteFill>
    </SceneTransition>
    <Caption lines={OUTRO} accent="cyan" />
  </AbsoluteFill>
);

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
